import { formatLocalDate } from '../utils/localDateTime';

import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import type { Itinerary, ItineraryStop } from '@types';
import AddressSelector from './AddressSelector';
import { ICONS } from './constants';

// Props match how the component is called from the IntegratedThreatAnalysis hub
interface ItineraryPlannerProps {
    onSave?: (itinerary: Itinerary) => void;
    onFormChange?: (change: { target: { name: string, value: any } }) => void;
    formData?: any;
    // The existingItineraries prop is kept for future use, but is not implemented in this version
    existingItineraries?: Itinerary[];
}

// This is a self-contained component that manages its own state
const ItineraryPlanner: React.FC<ItineraryPlannerProps> = ({ onSave, onFormChange, formData }) => {
    // Internal state for all itinerary data - initialized from formData if present
    const [name, setName] = useState(formData?.itinerary_name || '');
    const [principal, setPrincipal] = useState(formData?.principal_name || '');
    const [startDate, setStartDate] = useState(formData?.start_date || '');
    const [endDate, setEndDate] = useState(formData?.end_date || '');
    const [stops, setStops] = useState<ItineraryStop[]>(formData?.stops || []);
    const [openStopId, setOpenStopId] = useState<string | null>(null);

    // ✅ 1. DOWNWARD SYNC: Update local state if formData from parent changes (e.g. project load)
    useEffect(() => {
        if (formData) {
            if (formData.itinerary_name !== undefined && formData.itinerary_name !== name) setName(formData.itinerary_name);
            if (formData.principal_name !== undefined && formData.principal_name !== principal) setPrincipal(formData.principal_name);
            if (formData.start_date !== undefined && formData.start_date !== startDate) setStartDate(formData.start_date);
            if (formData.end_date !== undefined && formData.end_date !== endDate) setEndDate(formData.end_date);
            if (formData.stops !== undefined && JSON.stringify(formData.stops) !== JSON.stringify(stops)) {
                setStops(formData.stops);
            }
        }
    }, [formData]);

    // 2. UPWARD SYNC: Sync back to parent form whenever local state changes
    useEffect(() => {
        if (onFormChange) {
            const currentData = {
                itinerary_name: name,
                principal_name: principal,
                start_date: startDate,
                end_date: endDate,
                stops: stops
            };
            onFormChange({ target: { name: 'itinerary_all_data', value: currentData } });
            onFormChange({ target: { name: 'itinerary_name', value: name } });
            onFormChange({ target: { name: 'principal_name', value: principal } });
            onFormChange({ target: { name: 'start_date', value: startDate } });
            onFormChange({ target: { name: 'end_date', value: endDate } });
            onFormChange({ target: { name: 'stops', value: stops } });
        }
    }, [name, principal, startDate, endDate, stops]);

    // Local risk cache to avoid excessive Firestore reads
    const [riskCache, setRiskCache] = useState<Record<string, any>>({});
    
    useEffect(() => {
        const fetchStopRisks = async () => {
            const countriesToFetch = [...new Set(stops.map(s => s.destination?.country).filter(Boolean))];
            const newCache = { ...riskCache };
            let updated = false;

            for (const country of countriesToFetch) {
                if (country && !newCache[country]) {
                    try {
                        const q = query(collection(db, 'daily_country_analysis'), where('iso3', '==', country.toUpperCase()), limit(1));
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            newCache[country] = snap.docs[0].data();
                            updated = true;
                        }
                    } catch (e) {
                        console.error("Error fetching risk for", country, e);
                    }
                }
            }
            if (updated) setRiskCache(newCache);
        };
        fetchStopRisks();
    }, [stops]);

    const addStop = () => {
        const newId = `stop-${Date.now()}`;
        const newStop: ItineraryStop = {
            id: newId,
            stopName: `Travel Leg ${stops.length + 1}`,
            travelDate: '',
            modeOfTransport: 'Armored Vehicle Convoy',
            origin: {},
            destination: {},
            notes: '',
        };
        setStops([...stops, newStop]);
        setOpenStopId(newId);
    };

    const removeStop = (id: string) => {
        setStops(stops.filter(stop => stop.id !== id));
    };

    const handleStopChange = (id: string, field: keyof ItineraryStop, value: any) => {
        setStops(stops.map(stop => stop.id === id ? { ...stop, [field]: value } : stop));
    };
    
    const handleAddressChange = (stopId: string, addressType: 'origin' | 'destination', change: { target: { name: string, value: any } }) => {
        const { name: fieldName, value } = change.target;
        
        // Match both prefixed (e.g. origin_city) and non-prefixed (e.g. city) names
        // AddressSelector with prefix="" sends non-prefixed names
        const addressKey = fieldName.includes('_') ? fieldName.split('_').slice(1).join('_') : fieldName;
        
        setStops(prevStops => prevStops.map(stop => {
            if (stop.id === stopId) {
                return {
                    ...stop,
                    [addressType]: {
                        ...stop[addressType],
                        [addressKey]: value
                    }
                };
            }
            return stop;
        }));
    };

    const handleSave = () => {
        const newItinerary: Itinerary = {
            id: `itinerary-${Date.now()}`,
            name,
            principal,
            startDate,
            endDate,
            stops,
        };
        onSave?.(newItinerary);
    };

    return (
        <div className="space-y-4 p-4 bg-white rounded-lg shadow-inner">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Create New Itinerary Plan</h2>
            
            <div className="p-4 border rounded-lg bg-slate-50 space-y-4">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Itinerary / Project Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            placeholder="e.g., Executive Visit Q3"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Principal Name</label>
                        <input
                            type="text"
                            value={principal}
                            onChange={(e) => setPrincipal(e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            placeholder="e.g., John Doe"
                        />
                    </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    </div>
                 </div>
            </div>

            <div className="space-y-4 mt-6">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold text-slate-700">Travel Legs / Stops</h3>
                    <button 
                        type="button" 
                        onClick={addStop}
                        className="text-sm bg-slate-900 text-white px-3 py-1 rounded hover:bg-black transition-colors flex items-center gap-1"
                    >
                        <span>+ Add Stop</span>
                    </button>
                </div>

                {stops.length === 0 && (
                    <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                        <p className="text-slate-400 italic">No travel legs added yet. Click "+ Add Stop" to begin.</p>
                    </div>
                )}

                <div className="space-y-4">
                    {stops.map((stop, index) => (
                        <div key={stop.id} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white transition-all">
                            <div 
                                className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors ${openStopId === stop.id ? 'bg-indigo-50 border-b border-indigo-100' : ''}`}
                                onClick={() => setOpenStopId(openStopId === stop.id ? null : stop.id)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] font-bold">
                                        {index + 1}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-800">{stop.stopName || `Stop ${index + 1}`}</h4>
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">{formatLocalDate(stop.travelDate, 'Date TBD')} • {stop.modeOfTransport}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    {stop.destination?.country && riskCache[stop.destination.country] && (
                                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 rounded text-[9px] font-bold text-slate-600 border border-slate-200">
                                            <div className={`w-1.5 h-1.5 rounded-full ${Number(riskCache[stop.destination.country].riskScore) > 70 ? 'bg-red-500' : 'bg-emerald-500'}`} />
                                            RISK: {riskCache[stop.destination.country].riskScore}
                                        </div>
                                    )}
                                    <button 
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); removeStop(stop.id); }}
                                        className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                    <div className={`transition-transform duration-300 ${openStopId === stop.id ? 'rotate-180' : ''}`}>
                                        <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                                    </div>
                                </div>
                            </div>

                            {openStopId === stop.id && (
                                <div className="p-4 space-y-6 animate-in slide-in-from-top-2 duration-300">
                                    {stop.destination?.country && riskCache[stop.destination.country] && (
                                        <div className={`p-3 rounded-lg border flex items-center justify-between ${Number(riskCache[stop.destination.country].riskScore) > 70 ? 'bg-red-50/50 border-red-100' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${Number(riskCache[stop.destination.country].riskScore) > 70 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                                                    <AlertTriangleIcon className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-black uppercase text-slate-400 leading-none mb-1">Destination Risk Sentiment</p>
                                                    <h5 className="text-xs font-bold text-slate-800">{stop.destination.country} is currently rated {riskCache[stop.destination.country].riskTrend} @ {riskCache[stop.destination.country].riskScore}/100</h5>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[9px] font-bold text-slate-400 uppercase">Security Posture</p>
                                                <p className={`text-xs font-black ${Number(riskCache[stop.destination.country].riskScore) > 70 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                    {Number(riskCache[stop.destination.country].riskScore) > 70 ? 'HARDENED' : 'STANDARD'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 mb-1">Stop Name / Label</label>
                                            <input
                                                type="text"
                                                value={stop.stopName}
                                                onChange={(e) => handleStopChange(stop.id, 'stopName', e.target.value)}
                                                className="w-full bg-slate-50 border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 mb-1">Mode of Transport</label>
                                            <select
                                                value={stop.modeOfTransport}
                                                onChange={(e) => handleStopChange(stop.id, 'modeOfTransport', e.target.value)}
                                                className="w-full bg-slate-50 border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            >
                                                <option>Armored Vehicle Convoy</option>
                                                <option>Vetted Private Driver</option>
                                                <option>Commercial Flight</option>
                                                <option>Private Aviation</option>
                                                <option>Public Rail (High Speed)</option>
                                                <option>Maritime Vessel</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <h5 className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 bg-indigo-50 px-2 py-1 inline-block rounded">Origin / Pickup</h5>
                                            <AddressSelector 
                                                prefix="" 
                                                formData={stop.origin}
                                                onFormChange={(change) => handleAddressChange(stop.id, 'origin', change)}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <h5 className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 inline-block rounded">Destination / Drop-off</h5>
                                            <AddressSelector 
                                                prefix=""
                                                formData={stop.destination}
                                                onFormChange={(change) => handleAddressChange(stop.id, 'destination', change)}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 mb-1">Notes / Specific Security Requirements</label>
                                        <textarea
                                            value={stop.notes}
                                            onChange={(e) => handleStopChange(stop.id, 'notes', e.target.value)}
                                            className="w-full bg-slate-50 border border-slate-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {stops.length > 0 && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                    <button 
                        type="button"
                        onClick={handleSave} 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg shadow-sm transition-colors"
                    >
                        Save Itinerary Plan
                    </button>
                    <p className="text-[10px] text-slate-400 mt-2">Changes are automatically synced to the analysis form.</p>
                </div>
            )}
        </div>
    );
};

// Internal icon helpers for standalone use
const TrashIcon = ({ className }: { className: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
);

const ChevronDownIcon = ({ className }: { className: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
);

const AlertTriangleIcon = ({ className }: { className: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
);

export default ItineraryPlanner;
